import React, { useState } from 'react';
import { translate } from '../../Services/translation';
import Icon from '../Icon';
import Product from '../../Types/Product';
import SideMenu from '../SideMenu/SideMenu';
import RadioButton from '../Inputs/Radiobutton';
import { EditorStringModel } from '../../Types/EditorString';
import EditorString from '../EditorString/EditorString';

interface MaterialSelectorProps {
  currentProduct: Product;
  materials: Product[];
  currentMaterial: string;
  setCurrentMaterial: (string) => void;
  modalContent?: EditorStringModel;
}

interface RadioButtonLabelProps {
  title: string;
  description?: string;
  price?: string;
  campaignPrice?: string;
  unitOfMeasurement?: string;
}

const RadioButtonLabel = ({
  title,
  description,
  price,
  campaignPrice,
  unitOfMeasurement,
}: RadioButtonLabelProps) => {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex flex-col">
        <div className="h6">{title}</div>
        {description && <div className="text-md">{description}</div>}
      </div>
      {campaignPrice ? (
        <div className="whitespace-nowrap text-md text-campaign">
          ({campaignPrice}
          <span className="!ml-1 whitespace-nowrap text-md text-gray-500">
            / {unitOfMeasurement}
            <span className="!ml-1 line-through">{price}</span>
          </span>
          )
        </div>
      ) : (
        <div className="whitespace-nowrap text-md text-gray-500">
          ({price} / {unitOfMeasurement})
        </div>
      )}
    </div>
  );
};

const ProductPageMaterialSelector = ({
  currentProduct,
  materials,
  currentMaterial,
  setCurrentMaterial,
  modalContent,
}: MaterialSelectorProps) => {
  const [infoOpen, setInfoOpen] = useState<boolean>(false);

  return (
    <div className="mb-4 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="h5 flex-1">
          {translate('product-page.choose.material')}
        </div>
        {modalContent && (
          <>
            <button
              className="flex items-center"
              onClick={() => setInfoOpen(!infoOpen)}
            >
              <Icon
                name="question-light"
                className="mr-1 h-5 w-5 text-gray-700"
              />
              <span className="text-left text-sm font-normal uppercase tracking-wider underline">
                {translate('product-page.material.info')}
              </span>
            </button>
            <SideMenu
              id="materialGuide"
              open={infoOpen}
              title={' '}
              onClosed={() => setInfoOpen(false)}
              className={
                'm-2 !h-[calc(100%_-_1rem)] rounded md-down:w-[calc(100%_-_1rem)] [&_>div:first-child]:h-[52px] [&_>div]:!pl-0 [&_>div]:!pr-0'
              }
              useSideMenu={true}
            >
              <div className="pl-7 pr-7 lg:pl-[48px] lg:pr-[48px]">
                <h2 className="mb-6">
                  {translate('product-page.material.info')}
                </h2>
                <EditorString value={modalContent.value} />
              </div>
            </SideMenu>
          </>
        )}
      </div>
      <div className="flex flex-col gap-4">
        <RadioButton
          inputSize="medium"
          label={
            <RadioButtonLabel
              title={translate('product-page.standard.material.title')}
              description={translate(
                'product-page.standard.material.description'
              )}
              price={currentProduct.price.formattedPrice}
              campaignPrice={currentProduct.price.formattedCampaignPrice}
              unitOfMeasurement={currentProduct.unitOfMeasurement}
            />
          }
          id="standard"
          name="wallpaperMaterial"
          value=""
          checked={currentMaterial === ''}
          onChange={() => setCurrentMaterial('')}
        />
        {materials.map((material) => (
          <RadioButton
            key={material.id}
            inputSize="medium"
            label={
              <RadioButtonLabel
                title={material.name}
                description={material.description}
                campaignPrice={
                  currentProduct.price.campaignPrice
                    ? `${Math.round(
                        currentProduct?.price?.campaignPrice
                          ?.priceWithVat +
                          material.price.price?.priceWithVat!
                      )} ${currentProduct.price.currency.symbol}`
                    : undefined
                }
                price={`${Math.round(
                  material.price.price?.priceWithVat! +
                    currentProduct.price.price?.priceWithVat!
                )} ${currentProduct.price.currency.symbol}`}
                unitOfMeasurement={
                  material.unitOfMeasurement ||
                  currentProduct.unitOfMeasurement
                }
              />
            }
            id={material.id}
            name="wallpaperMaterial"
            value={material.id}
            checked={currentMaterial === material.id}
            onChange={() => setCurrentMaterial(material.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default ProductPageMaterialSelector;
